import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

const EASE = [0.22, 1, 0.36, 1];

export const AnchorLink = ({ href, className, children, testid, onNavigate }) => {
  const handle = (e) => {
    e.preventDefault();
    if (onNavigate) onNavigate();
    const el = document.querySelector(href);
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (window.__lenis && !reduce) window.__lenis.scrollTo(el, { offset: -116, duration: 1.15 });
    else el.scrollIntoView({ behavior: 'smooth' });
  };
  return (
    <a href={href} data-testid={testid} className={className} onClick={handle}>
      {children}
    </a>
  );
};

export const Reveal = ({ children, delay = 0, y = 28, className, testid }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      data-testid={testid}
      className={className}
      initial={reduce ? false : { opacity: 0, y }}
      whileInView={reduce ? {} : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.75, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
};

/* Masked line-by-line headline reveal */
export const MaskLines = ({ lines, className, lineClassName, delay = 0, highlightIndex = -1 }) => {
  const reduce = useReducedMotion();
  const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.13, delayChildren: delay } },
  };
  const line = {
    hidden: { y: '112%' },
    show: { y: '0%', transition: { duration: 0.95, ease: EASE } },
  };
  return (
    <motion.span
      className={className}
      variants={container}
      initial={reduce ? 'show' : 'hidden'}
      animate="show"
      style={{ display: 'block' }}
    >
      {lines.map((l, i) => (
        <span key={i} style={{ display: 'block', overflow: 'hidden', paddingBottom: '0.08em' }}>
          <motion.span
            variants={reduce ? {} : line}
            className={lineClassName}
            style={{ display: 'inline-block', willChange: 'transform' }}
          >
            {i === highlightIndex ? (
              <span className="relative inline-block">
                <span className="relative z-10">{l}</span>
                <motion.span
                  aria-hidden="true"
                  className="absolute left-0 bottom-[0.06em] -z-0 h-[0.42em] w-full rounded-sm bg-lime"
                  initial={reduce ? false : { scaleX: 0 }}
                  animate={reduce ? {} : { scaleX: 1 }}
                  transition={{ duration: 0.7, delay: delay + 0.75 + i * 0.13, ease: EASE }}
                  style={{ transformOrigin: 'left' }}
                />
              </span>
            ) : (
              l
            )}
          </motion.span>
        </span>
      ))}
    </motion.span>
  );
};

/* Mono eyebrow label with lime tick + optional index */
export const Eyebrow = ({ index, children, light = false }) => (
  <span
    className={`inline-flex items-center gap-3 font-mono text-[11px] sm:text-xs font-semibold uppercase tracking-[0.28em] ${
      light ? 'text-white/70' : 'text-royal'
    }`}
  >
    <span aria-hidden="true" className="inline-block h-2 w-2 rotate-45 bg-lime" />
    {index && <span className={light ? 'text-lime' : 'text-lime.deep'}>{index}</span>}
    {children}
  </span>
);

export const ArrowLink = ({ href, children, className = '', testid, accent = 'text-royal', onNavigate }) => (
  <AnchorLink
    href={href}
    testid={testid}
    onNavigate={onNavigate}
    className={`group inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-[0.14em] ${accent} ${className}`}
  >
    <span className="link-slide">{children}</span>
    <svg
      className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M7 17 17 7" />
      <path d="M8 7h9v9" />
    </svg>
  </AnchorLink>
);
